'use client';

import React from 'react';
import Layout from '../../components/Layout';
import Link from 'next/link';
import SubDocsLayout from '../../components/SubDocsLayout';
import useTranslation from 'next-translate/useTranslation';
import SEOHead from '../../components/SEOHead';
import { useLocalePath } from '../../lib/useLocalePath';
import { useTheme } from '../../lib/ThemeContext';

const Cookies = () => {
  const { lang } = useTranslation('common');
  const { prefix } = useLocalePath();
  const { theme } = useTheme();
  const isLt = lang === 'lt';
  const isDarkTheme = theme === 'dark';
  const privacyHref = prefix ? `${prefix}/sub/privacy-policy` : '/sub/privacy-policy';

  const headingClass = isDarkTheme ? 'text-gray-100' : 'text-gray-900';
  const bodyClass = isDarkTheme ? 'text-gray-300' : 'text-gray-700';

  return (
    <Layout>
      <SEOHead
        title={isLt ? 'Slapukų politika | GadgetFever' : 'Cookie Policy | GadgetFever'}
        description={
          isLt
            ? 'Kaip GadgetFever naudoja slapukus užsakymų procesui, saugumui ir analitikai pagal GDPR reikalavimus.'
            : 'How GadgetFever uses cookies for checkout, security, and analytics in compliance with GDPR.'
        }
        keywords='cookies, cookie policy, gdpr, ecommerce, dropshipping, gadgetfever'
        canonicalUrl={isLt ? '/lt/sub/slapukai' : '/sub/cookies-info'}
        lang={lang}
      />

      <SubDocsLayout>
        <h1 className={`text-2xl sm:text-3xl font-semibold mb-4 text-left ${headingClass}`}>
          {isLt ? 'Slapukų politika' : 'Cookie Policy'}
        </h1>
        <p className={`text-base mb-8 text-left leading-relaxed ${bodyClass}`}>
          {isLt
            ? 'Slapukai padeda užtikrinti saugų apsipirkimą, išsaugoti jūsų pasirinkimus ir pagerinti svetainės veikimą.'
            : 'Cookies help us provide secure checkout, remember your preferences, and improve site performance.'}
        </p>

        <ul className='space-y-6 mb-10'>
          <li className='flex gap-4'>
            <span className={`flex-shrink-0 w-2 h-2 rounded-full mt-2 ${isDarkTheme ? 'bg-gray-300' : 'bg-gray-500'}`} />
            <div className={bodyClass}>
              <span className={`font-semibold ${headingClass}`}>{isLt ? 'Būtini slapukai:' : 'Essential cookies:'}</span>{' '}
              {isLt
                ? 'Reikalingi krepšeliui, prisijungimui ir užsakymo procesui. Be jų parduotuvė tinkamai neveiks.'
                : 'Required for cart, login, and checkout functionality. Without these, the store cannot operate correctly.'}
            </div>
          </li>
          <li className='flex gap-4'>
            <span className={`flex-shrink-0 w-2 h-2 rounded-full mt-2 ${isDarkTheme ? 'bg-gray-300' : 'bg-gray-500'}`} />
            <div className={bodyClass}>
              <span className={`font-semibold ${headingClass}`}>{isLt ? 'Analitiniai slapukai:' : 'Analytics cookies:'}</span>{' '}
              {isLt
                ? 'Naudojami tik gavus sutikimą. Jie padeda suprasti, kaip lankytojai naudoja puslapius ir kur tobulinti pirkimo kelią.'
                : 'Used only with your consent. They help us understand visitor behavior and improve the shopping flow.'}
            </div>
          </li>
          <li className='flex gap-4'>
            <span className={`flex-shrink-0 w-2 h-2 rounded-full mt-2 ${isDarkTheme ? 'bg-gray-300' : 'bg-gray-500'}`} />
            <div className={bodyClass}>
              <span className={`font-semibold ${headingClass}`}>{isLt ? 'Nuostatų slapukai:' : 'Preference cookies:'}</span>{' '}
              {isLt
                ? 'Leidžia prisiminti kalbą ir kitus pasirinkimus, kad kitą kartą apsilankius patirtis būtų patogesnė.'
                : 'Remember language and selected options to provide a smoother experience on future visits.'}
            </div>
          </li>
        </ul>

        <p className={`text-sm text-left ${isDarkTheme ? 'text-gray-400' : 'text-gray-500'}`}>
          {isLt ? 'Daugiau informacijos rasite mūsų ' : 'For full details, read our '}
          <Link href={privacyHref} className={isDarkTheme ? 'text-gray-100 underline hover:no-underline' : 'text-primary underline hover:no-underline'}>
            {isLt ? 'Privatumo politikoje' : 'Privacy Policy'}
          </Link>
          .
        </p>
      </SubDocsLayout>
    </Layout>
  );
};

export default Cookies;
